import React, { useState, useEffect, useMemo } from "react";
import {
  Award,
  Heart,
  CheckCircle,
  Edit3,
  Users,
  Calendar,
  Rocket,
} from "lucide-react";
import { EditProfileModal } from "./UI";
import { getCategoryById } from "../services/CategoryModel";
import { uploadSeedData } from "../services/seedDatabase";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { db } from "../services/firebase";
import { fetchUsersByIds } from "../services/meetlyService";
import { getCurrentUserId } from "../services/userService";
import { useUser } from "../context/user";
import { collection, query, where, onSnapshot } from "firebase/firestore";

// Import independent CSS module for styling
import styles from "./Profile.module.css";

// Renders the current user's own profile: stats, interests, created events,
// joined communities/events, and connections/matches. userData/setUserData
// come from UserContext; everything else (event/community lists, counts)
// is passed down from Home.jsx, already filtered to "this user's" subset.
export default function ProfileScreen({
  joinedEventsCount,
  joinedEventsList = [],
  communitiesList = [],
  matchesList = [],
  myCreatedEvents = [],
  onLogout,
  onSelectEvent,
  onSelectCommunity,
  onSelectConnection, // Callback to navigate to the connection's profile
  setIsCreatingEvent,
  setIsCreatingCommunity,
}) {
  const { userData, setUserData } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [firestoreConnections, setFirestoreConnections] = useState([]);

  // Effect: Listen to active chats/connections from Firestore in real-time
  useEffect(() => {
    const currentUid = getCurrentUserId(userData);
    if (!currentUid) return;

    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", currentUid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        // First pass: no network calls, just collect the other participant's
        // id for every chat in this snapshot.
        const chatEntries = snapshot.docs
          .map((change) => {
            const chatData = change.data();
            const participants = chatData.participants || [];
            const otherUserId = participants.find((id) => id !== currentUid);
            return { chatData, otherUserId };
          })
          .filter((entry) => entry.otherUserId);

        // Single batched query instead of one getDoc() per connection.
        const usersMap = await fetchUsersByIds(
          chatEntries.map((entry) => entry.otherUserId)
        );

        const connectionsMap = new Map();
        chatEntries.forEach(({ chatData, otherUserId }) => {
          const profileData = usersMap.get(otherUserId) || {};
          connectionsMap.set(otherUserId, {
            id: otherUserId,
            uid: otherUserId,
            ...profileData,
            name: profileData.name || profileData.displayName || "User",
            image: getAvatarUrl(profileData),
            lastMessage: chatData.lastMessage || "",
          });
        });

        setFirestoreConnections(Array.from(connectionsMap.values()));
      } catch (error) {
        console.error("Error fetching live profile connections:", error);
      }
    });

    return () => unsubscribe();
  }, [userData]);

  // Combine standard matches with live Firestore connections (avoiding duplicates)
  // Memoized: this only needs to recompute when the source lists actually change,
  // not on every render (e.g. while typing in the Edit Profile modal).
  const totalConnections = useMemo(() => {
    const matchesMap = new Map();
    matchesList.forEach((m) => {
      const id = m.id || m.uid;
      if (id) matchesMap.set(id, m);
    });
    firestoreConnections.forEach((c) => {
      const id = c.id || c.uid;
      if (id && !matchesMap.has(id)) {
        matchesMap.set(id, c);
      }
    });
    return Array.from(matchesMap.values());
  }, [matchesList, firestoreConnections]);

  // Filter communities joined by the current user
  const joinedCommunities = useMemo(
    () =>
      communitiesList.filter((c) =>
        userData?.joinedCommunities?.includes(c.id)
      ),
    [communitiesList, userData?.joinedCommunities]
  );

  // HELPERS
  const calculateAge = (birthDate) =>
    birthDate
      ? new Date().getFullYear() - new Date(birthDate).getFullYear()
      : 0;

  return (
    <div className={styles.container}>
      {/* Profile Header: Avatar & User Details */}
      <div className={styles.headerSection}>
        <div className={styles.avatarWrapper}>
          <img src={getAvatarUrl(userData)} alt="Avatar" />
        </div>
        <h2 className={styles.userName}>
          {userData.name || "User"}, {calculateAge(userData.birthDate)}
        </h2>
        <p className={styles.userGender}>{userData.gender}</p>

        {/* User bio, shown only when set */}
        {userData.bio && <p className={styles.userBio}>"{userData.bio}"</p>}

        <p className={styles.verifiedBadge}>
          <CheckCircle size={16} /> Verified User
        </p>

        {setUserData && (
          <button
            onClick={() => setIsEditing(true)}
            className={styles.editProfileButton}
          >
            <Edit3 size={16} /> Edit Profile
          </button>
        )}
      </div>

      {/* Seed Database Button */}
      {process.env.NODE_ENV === "development" && (
        <button onClick={uploadSeedData} className={styles.seedButton}>
          <Rocket size={16} /> Seed Database (Dev Only)
        </button>
      )}

      {/* Quick creation action buttons */}
      <div className={styles.quickActionGrid}>
        <button
          onClick={() => setIsCreatingEvent && setIsCreatingEvent(true)}
          className={styles.quickEventBtn}
        >
          <Calendar size={16} /> Create Event
        </button>
        <button
          onClick={() => setIsCreatingCommunity && setIsCreatingCommunity(true)}
          className={styles.quickCommunityBtn}
        >
          <Users size={16} /> New Community
        </button>
      </div>

      {/* User Statistics Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <Award className={styles.statIcon} />
          <p className={styles.statValue}>{joinedEventsCount}</p>
          <p className={styles.statLabel}>Events Joined</p>
        </div>
        <div className={styles.statCard}>
          <Users className={styles.statIcon} />
          <p className={styles.statValue}>{joinedCommunities.length}</p>
          <p className={styles.statLabel}>Communities</p>
        </div>
        <div className={styles.statCard}>
          <Heart className={styles.statIcon} />
          <p className={styles.statValue}>{totalConnections.length}</p>
          <p className={styles.statLabel}>Matches</p>
        </div>
      </div>

      {/* User Interests Section */}
      <div>
        <h3 className={styles.interestsTitle}>Interests</h3>
        <div className={styles.interestsContainer}>
          {userData.interests && userData.interests.length > 0 ? (
            userData.interests.map((interestId) => {
              const catObj = getCategoryById(interestId);
              return (
                <span key={interestId} className={styles.interestTag}>
                  {catObj.icon} {catObj.label}
                </span>
              );
            })
          ) : (
            <p className={styles.emptyText}>No interests selected yet.</p>
          )}
        </div>
      </div>

      {/*My Created Events Section*/}
      <div>
        <h3 className={styles.interestsTitle}>
          My Created Events ({myCreatedEvents.length})
        </h3>
        {myCreatedEvents.length === 0 ? (
          <p className={styles.emptyText}>
            You haven't created any events yet.
          </p>
        ) : (
          <div className={styles.eventsList}>
            {myCreatedEvents.map((ev) => (
              <div
                key={ev.id}
                onClick={() => onSelectEvent && onSelectEvent(ev)}
                className={styles.eventItem}
              >
                <img
                  src={
                    ev.image ||
                    "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&auto=format&fit=crop&q=60"
                  }
                  alt={ev.title}
                  className={styles.eventThumb}
                />
                <div className={styles.eventInfo}>
                  <h4 className={styles.eventTitle}>{ev.title}</h4>
                  <p className={styles.eventCategory}>📍 {ev.location}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Joined Communities Section */}
      <div>
        <h3 className={styles.interestsTitle}>My Communities</h3>
        {joinedCommunities.length === 0 ? (
          <p className={styles.emptyText}>
            You haven't joined any communities yet.
          </p>
        ) : (
          <div className={styles.eventsList}>
            {joinedCommunities.map((comm) => (
              <div
                key={comm.id}
                onClick={() => onSelectCommunity && onSelectCommunity(comm)}
                className={styles.eventItem}
              >
                <img
                  src={
                    comm.image ||
                    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&auto=format&fit=crop&q=60"
                  }
                  alt={comm.name}
                  className={styles.eventThumb}
                />
                <div className={styles.eventInfo}>
                  <h4 className={styles.eventTitle}>{comm.name}</h4>
                  <p className={styles.eventCategory}>
                    {Array.isArray(comm.members)
                      ? `${comm.members.length} members`
                      : typeof comm.members === "number"
                      ? `${comm.members} members`
                      : comm.members || "Community"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Joined Events Section */}
      <div>
        <h3 className={styles.interestsTitle}>My Joined Events</h3>
        {joinedEventsList.length === 0 ? (
          <p className={styles.emptyText}>You haven't joined any events yet.</p>
        ) : (
          <div className={styles.eventsList}>
            {joinedEventsList.map((ev) => {
              const evCatObj = getCategoryById(ev.category);
              return (
                <div
                  key={ev.id}
                  onClick={() => onSelectEvent && onSelectEvent(ev)}
                  className={styles.eventItem}
                >
                  <img
                    src={
                      ev.image ||
                      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&auto=format&fit=crop&q=60"
                    }
                    alt={ev.title}
                    className={styles.eventThumb}
                  />
                  <div className={styles.eventInfo}>
                    <h4 className={styles.eventTitle}>{ev.title}</h4>
                    <p className={styles.eventCategory}>
                      {evCatObj.icon} {evCatObj.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* My Connections / Matches Section */}
      <div>
        <h3 className={styles.interestsTitle}>
          Connections ({totalConnections.length})
        </h3>
        {totalConnections.length === 0 ? (
          <p className={styles.emptyText}>
            No connections yet. Connect at events or accept invitations!
          </p>
        ) : (
          <div className={styles.matchesScroll}>
            {totalConnections.map((m) => (
              <div
                key={m.id || m.name}
                onClick={() => onSelectConnection && onSelectConnection(m)}
                className={styles.matchAvatarItem}
                title={`View ${m.name || "User"}'s profile`}
              >
                <div className={styles.matchAvatarRing}>
                  <img src={getAvatarUrl(m)} alt={m.name || "User"} />
                </div>
                <span className={styles.matchName}>{m.name || "User"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logout Action Button */}
      <button onClick={onLogout} className={styles.logoutButton}>
        Log Out
      </button>

      {/* Edit Profile Modal */}
      {isEditing && (
        <EditProfileModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          onSave={async (updated) => {
            if (setUserData && updated) setUserData(updated);
            setIsEditing(false);
          }}
          userData={userData}
          setUserData={setUserData}
        />
      )}
    </div>
  );
}

